import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import StatusCode from "../../../util/StatusCode";
import ActiveToggle from "../../misc/ActiveToggle";
import TableHorizontalPagedToggle, { InfoObject } from "../../misc/TableHorizontalPagedToggle";
import * as Helper from '../../../util/Helper';
import { GetStudentGroups, GetTeacherGroups, UserGroupsRequestState } from "../../../store/UserAction";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../store/store";



const UserViewGroups = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { userId } = useParams();
    const dispatch = useDispatch();
    const [show, setShow] = useState({ student: true, teacher: true });

    const teacherGroups = useSelector<RootState, UserGroupsRequestState>(state => state.users.teacherGroups);
    const studentGroups = useSelector<RootState, UserGroupsRequestState>(state => state.users.studentGroups);

    useEffect(() => {
        if (userId) {
            GetTeacherGroups(userId, {})(dispatch);
            GetStudentGroups(userId, {})(dispatch);
        }
    }, [dispatch, userId]);

    const updateTeacherGroupsPage = (value: number) => {
        if (userId) {
            GetTeacherGroups(userId, { ...teacherGroups.filter, page: value })(dispatch);
        }
    }

    const updateStudentGroupsPage = (value: number) => {
        if (userId) {
            GetStudentGroups(userId, { ...studentGroups.filter, page: value })(dispatch);
        }
    }

    const updateOnlyActive = (value: boolean) => {
        if (userId) {
            GetStudentGroups(userId, { ...studentGroups.filter, onlyActive: value, page: 1 })(dispatch);
            GetTeacherGroups(userId, { ...teacherGroups.filter, onlyActive: value, page: 1 })(dispatch);
        }
    }

    const mapTeacherGroups = (groups: UserGroupsRequestState): InfoObject[] => {
        if (groups.code !== StatusCode.COMPLETE) {
            return [];
        }

        return groups.data.values.map(x => ({
            id: x.id,
            data: [
                { value: <Link to={`/group/${x.id}/information`}>{x.title}</Link> },
                { value: <Link to={`/schoolunit/${x.schoolUnit.id}/information`}>{x.schoolUnit.title}</Link> },
                { value: t('groupTypes.' + x.groupType.id, { ns: 'schoolAdministration' }) },
                { value: t('assignmentRoleTypes.' + x.role, { ns: 'schoolAdministration' }) },
                { value: Helper.active(x.active) },
                { value: Helper.formatDateOnly(x.startDate) },
                { value: Helper.formatDateOnly(x.endDate) },
            ]
        }));
    }

    const mapStudentGroups = (groups: UserGroupsRequestState): InfoObject[] => {
        if (groups.code !== StatusCode.COMPLETE) {
            return [];
        }

        return groups.data.values.map(x => ({
            id: x.id,
            data: [
                { value: <Link to={`/group/${x.id}/information`}>{x.title}</Link> },
                { value: <Link to={`/schoolunit/${x.schoolUnit.id}/information`}>{x.schoolUnit.title}</Link> },
                { value: t('groupTypes.' + x.groupType.id, { ns: 'schoolAdministration' }) },
                { value: Helper.active(x.active) },
                { value: Helper.formatDateOnly(x.startDate) },
                { value: Helper.formatDateOnly(x.endDate) },
            ]
        }));
    }

    const renderTeacherGroups = () => {
        const teacherInfo = mapTeacherGroups(teacherGroups);
        const count = Helper.getTotalCountText(teacherGroups);
        return (
            <TableHorizontalPagedToggle
                header={[{ value: `${t('connections.staff', { ns: 'schoolAdministration', count: 1 })} ${count}` }, { value: t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }) }, { value: t('commonTableHeaders.groupType', { ns: 'schoolAdministration' }) }, { value: t('commonTableHeaders.role', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('genericTableHeaders.startDate', { ns: 'common' }) }, { value: t('genericTableHeaders.endDate', { ns: 'common' }) }]}
                mobileHeader={[t('genericTableHeaders.name', { ns: 'common' }), t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }), t('commonTableHeaders.groupType', { ns: 'schoolAdministration' }), t('commonTableHeaders.role', { ns: 'schoolAdministration' }), t('genericTableHeaders.active', { ns: 'common' }), t('genericTableHeaders.startDate', { ns: 'common' }), t('genericTableHeaders.endDate', { ns: 'common' })]}
                info={teacherInfo}
                show={show.teacher}
                toggle={() => setShow({ ...show, teacher: !show.teacher })}
                displayName={`${t('connection.staff', { ns: 'schoolAdministration', count: 2 })} ${count}`}
                onChangePage={updateTeacherGroupsPage}
                noResultMsg={t('userViewGroups.staff.noResult', { ns: 'schoolAdministration' })}
                requestState={teacherGroups}
            />
        )
    }

    const renderStudentGroups = () => {
        const studentInfo = mapStudentGroups(studentGroups);
        const count = Helper.getTotalCountText(studentGroups);
        return (
            <TableHorizontalPagedToggle
                header={[{ value: `${t('connections.student', {ns: 'schoolAdministration', count: 1})} ${count}` }, { value: t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }) }, { value: t('commonTableHeaders.groupType', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('genericTableHeaders.startDate', { ns: 'common' }) }, { value: t('genericTableHeaders.endDate', { ns: 'common' }) }]}
                mobileHeader={[t('genericTableHeaders.name', { ns: 'common' }), t('commonTableHeaders.schoolUnit', { ns: 'schoolAdministration' }), t('commonTableHeaders.groupType', { ns: 'schoolAdministration' }), t('genericTableHeaders.active', { ns: 'common' }), t('genericTableHeaders.startDate', { ns: 'common' }), t('genericTableHeaders.endDate', { ns: 'common' })]}
                info={studentInfo}
                show={show.student}
                toggle={() => setShow({ ...show, student: !show.student })}
                displayName={`${t('connections.student', { ns: 'schoolAdministration', count: 2 })} ${count}`}
                onChangePage={updateStudentGroupsPage}
                noResultMsg={t('userViewGroups.student.noResult', { ns: 'schoolAdministration' })}
                requestState={studentGroups}
            />
        )
    }

    return (<>
        <div className="header_member_table large">{t('object.schoolAdmin.group', { ns: 'common', count: 2 })}
            <span className="right">
                <ActiveToggle
                    onlyActive={studentGroups.filter.onlyActive ?? true}
                    update={updateOnlyActive}
                    disabled={studentGroups.code === StatusCode.PENDING || teacherGroups.code === StatusCode.PENDING}
                />
            </span></div>
        {renderTeacherGroups()}
        {renderStudentGroups()}
    </>);
}

export default UserViewGroups;
